import { Dialog, DialogContent } from '@mui/material'
import { useState } from 'react'
import { MemberDto, MemberService } from '@services/api'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import { MemberRoleEnumLabels } from '@utils/constants'
import { FormHeader } from '../forms/FormHeader'
import { UpTransition } from '../animations'
import {
  completeGeneratorElutionQualityControl,
  completePreparationLotQualityControl,
} from '@state/reducers/operationQualityControlsReducer'
import ServerAutocomplete from '@components/autocomplete/ServerAutocomplete'
import { enqueueSnackbar } from '@state/reducers/alertReducer'

interface Props {
  open: boolean
  onClose: () => void
  entityType: 'preparationLot' | 'generatorElution'
  entityId: string
}

export function CompleteQcDialog({ open, onClose, entityType, entityId }: Props) {
  const dispatch = useAppDispatch()
  const user = useAppSelector(({ auth }) => auth.user)
  const submitting = useAppSelector(({ operationQualityControls }) => operationQualityControls.submitting)
  const currentMember = user && 'role' in user ? user as unknown as MemberDto : null
  const [completedByMember, setCompletedByMember] = useState<MemberDto | null>(currentMember)

  const fetchMembers = async (search: string) => {
    const response = await MemberService.findAll({ search, limit: 20 })
    return response?.datas || []
  }

  const getMemberLabel = (member: MemberDto | null) => {
    if (!member) return ''
    const roleLabel = MemberRoleEnumLabels[member.role]
    return `${member.firstName} ${member.lastName}${roleLabel ? ` (${roleLabel})` : ''}`
  }

  const handleClose = () => {
    setCompletedByMember(currentMember)
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!completedByMember) return
    try {
      const action = entityType === 'preparationLot'
        ? completePreparationLotQualityControl({ entityId, completedByMemberId: completedByMember.id })
        : completeGeneratorElutionQualityControl({ entityId, completedByMemberId: completedByMember.id })
      await dispatch(action).unwrap()
      onClose()
    } catch {
      dispatch(enqueueSnackbar({ message: 'Une erreur est survenue', options: { variant: 'error' } }))
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth TransitionComponent={UpTransition}>
      <DialogContent className="p-0">
        <form onSubmit={handleSubmit}>
          <div className="flex flex-col gap-4 p-6">
            <FormHeader
              title={`${entityType === 'preparationLot' ? 'Préparation' : 'Élution'} conforme`}
              onClose={handleClose}
              isSubmitting={submitting}
              submitText="Confirmer"
              canSubmit={!!completedByMember}
            />
            <ServerAutocomplete<MemberDto>
              width="100%"
              defaultValue={currentMember}
              initialOptions={currentMember ? [currentMember] : []}
              value={completedByMember}
              fetchService={fetchMembers}
              onChange={(value: MemberDto | null) => setCompletedByMember(value)}
              getValue={(value) => value?.id?.toString() || null}
              getOptionLabel={(value) => getMemberLabel(value as MemberDto | null)}
              debounceMs={300}
              placeholder="Rechercher un membre..."
              label="Validé par"
              minSearchLength={0}
            />
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
